import React, {useState} from 'react';
import {interpolate} from './api';
import {sanitizeHtml} from './RichTextArea';

const looksLikeHtml = (text) => /<[a-z][\s\S]*>/i.test(text || '');

// Screen 2 — AI Review (FR-017 – FR-025). The reduced variant (irrelevant / clarification_needed) shows
// the message and the original text with Back and Skip only (FR-018).
export default function ReviewScreen({t, review, maxSkus, busy, errors, onApprove, onBack, onSkip}) {
    const ambiguous = review.outcome === 'ambiguous_draft';
    const [draft, setDraft] = useState(review.draft_description || '');
    const [editing, setEditing] = useState(false);
    const [skus, setSkus] = useState(() => (review.suggested_skus || []).map((s) => ({...s})));
    const [adding, setAdding] = useState(false);
    const [newType, setNewType] = useState('');

    const selectedCount = skus.filter((s) => s.selected).length;
    const canApprove = !busy && selectedCount > 0 && draft.trim().length > 0;

    const toggle = (index) => setSkus((list) => list.map((s, i) => {
        if (i !== index) return s;
        if (!s.selected && selectedCount >= maxSkus) return s;
        return {...s, selected: !s.selected};
    }));

    const addType = () => {
        const label = newType.trim();
        const duplicate = skus.some((s) => s.label.toLowerCase() === label.toLowerCase());
        if (label && !duplicate) {
            setSkus((list) => [...list, {label, selected: selectedCount < maxSkus}]);
        }
        setNewType('');
        setAdding(false);
    };

    const onAddKey = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            addType();
        } else if (e.key === 'Escape') {
            setNewType('');
            setAdding(false);
        }
    };

    return (
        <div className="di-page di-page--review">
            <div className="di-banner">
                <span aria-hidden="true">⚠ </span>{review.banner}
                {review.failed_part && (
                    <div className="di-banner__part">{interpolate(t.failed_part, {part: review.failed_part})}</div>
                )}
            </div>

            <div className="di-quote-card">
                <div className="di-quote-card__label">{t.original_text_label}</div>
                {/* The intake box stores sanitised HTML (tables, bold, lists); render it, never show the tags. */}
                {looksLikeHtml(review.original_text) ? (
                    <blockquote className="di-quote di-quote--html"
                                dangerouslySetInnerHTML={{__html: sanitizeHtml(review.original_text)}}/>
                ) : (
                    <blockquote className="di-quote">“{review.original_text}”</blockquote>
                )}
            </div>

            {ambiguous && (
                <div className="di-draft-card">
                    <div className="di-draft-card__label">{t.draft_label}</div>
                    <div className="di-draft-card__body">
                        {editing ? (
                            <textarea className="di-textarea di-textarea--light" rows={5} value={draft}
                                      onChange={(e) => setDraft(e.target.value)}/>
                        ) : (
                            <p className="di-draft-card__text">{draft}</p>
                        )}

                        <div className="di-skus">
                            <div className="di-skus__label">{t.skus_label}</div>
                            <div className="di-skus__note">{t.skus_note}</div>
                            <ul className="di-skus__list" role="group">
                                {skus.map((sku, index) => (
                                    <li key={sku.label}>
                                        <button type="button" role="checkbox" aria-checked={sku.selected}
                                                className={`di-sku ${sku.selected ? 'di-sku--checked' : ''}`}
                                                onClick={() => toggle(index)}>
                                            <span className="di-sku__box" aria-hidden="true">
                                                {sku.selected && (
                                                    <svg viewBox="0 0 16 16" width="12" height="12">
                                                        <path d="M3 8.5l3 3 7-7" stroke="#fff" strokeWidth="2" fill="none"/>
                                                    </svg>
                                                )}
                                            </span>
                                            <span className="di-sku__label">{sku.label}</span>
                                        </button>
                                    </li>
                                ))}
                            </ul>
                            {selectedCount >= maxSkus && (
                                <div className="di-skus__note">{interpolate(t.max_skus, {max: maxSkus})}</div>
                            )}
                            {adding ? (
                                <div className="di-add-type">
                                    <input autoFocus className="di-input" value={newType} placeholder={t.add_type_placeholder}
                                           onChange={(e) => setNewType(e.target.value)} onKeyDown={onAddKey}/>
                                    <button type="button" className="di-add-type__add" onClick={addType}>{t.add}</button>
                                    <button type="button" className="di-link"
                                            onClick={() => { setNewType(''); setAdding(false); }}>{t.cancel}</button>
                                </div>
                            ) : (
                                <button type="button" className="di-link di-link--cyan" onClick={() => setAdding(true)}>
                                    {t.add_type}
                                </button>
                            )}
                        </div>

                        <div className="di-draft-card__actions">
                            <button type="button" className="di-outline" onClick={() => setEditing(!editing)}>
                                {editing ? t.done_editing : t.edit_draft}
                            </button>
                            <button type="button" className="di-cta di-cta--inline" disabled={!canApprove}
                                    onClick={() => onApprove(draft, skus.filter((s) => s.selected).map((s) => s.label))}>
                                {t.approve}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {errors.length > 0 && <div className="di-alert">{errors.map((e) => <div key={e}>{e}</div>)}</div>}

            <div className="di-footer">
                {ambiguous && <p className="di-footer__note">{t.generative_mode}</p>}
                <button type="button" className="di-link" onClick={onBack}>{t.back}</button>
                {!ambiguous && <button type="button" className="di-link" onClick={onSkip}>{t.skip}</button>}
            </div>
        </div>
    );
}
